import { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Box, Paper, Typography } from "@mui/material";
import {
  filterMembersForMention,
  getMentionState,
  insertMention,
} from "../utils/dailyNotebookMentions";

const composerStyle = {
  width: "100%",
  minHeight: 56,
  maxHeight: 140,
  padding: "12px 14px",
  border: "1px solid #e7e5e4",
  borderRadius: 10,
  fontSize: "0.95rem",
  lineHeight: 1.5,
  fontFamily: "'Inter', ui-sans-serif, system-ui, sans-serif",
  resize: "none",
  outline: "none",
  background: "#fff",
  boxSizing: "border-box",
};

const AUTO_GROW_MAX_HEIGHT = 240;

export default function MentionComposer({
  value,
  onChange,
  onSubmit,
  members = [],
  placeholder = "Write a line… type @ to tag someone",
  disabled = false,
  // Opt-in: grow with the text up to a cap instead of staying two lines tall,
  // so a pasted multi-line block is readable. Other composers keep their size.
  autoGrow = false,
}) {
  const textareaRef = useRef(null);
  const [mentionOpen, setMentionOpen] = useState(false);
  const [mentionStart, setMentionStart] = useState(null);
  const [mentionQuery, setMentionQuery] = useState("");
  const [activeIdx, setActiveIdx] = useState(0);

  useLayoutEffect(() => {
    if (!autoGrow) return;
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, AUTO_GROW_MAX_HEIGHT)}px`;
  }, [autoGrow, value]);

  const suggestions = useMemo(
    () => filterMembersForMention(members, mentionQuery),
    [members, mentionQuery]
  );

  const syncMention = useCallback((text, cursor) => {
    const state = getMentionState(text, cursor);
    if (!state) {
      setMentionOpen(false);
      setMentionStart(null);
      setMentionQuery("");
      setActiveIdx(0);
      return;
    }
    setMentionOpen(true);
    setMentionStart(state.start);
    setMentionQuery(state.query);
    setActiveIdx(0);
  }, []);

  const pickMember = useCallback(
    (member) => {
      const el = textareaRef.current;
      if (!el || mentionStart == null) return;
      const { text, cursor } = insertMention(
        value,
        mentionStart,
        el.selectionStart,
        member.mentionInsert
      );
      onChange(text);
      setMentionOpen(false);
      requestAnimationFrame(() => {
        el.focus();
        el.setSelectionRange(cursor, cursor);
      });
    },
    [mentionStart, onChange, value]
  );

  const handleChange = (e) => {
    const next = e.target.value;
    onChange(next);
    syncMention(next, e.target.selectionStart);
  };

  const handleKeyDown = (e) => {
    if (mentionOpen && suggestions.length > 0) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActiveIdx((i) => (i + 1) % suggestions.length);
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setActiveIdx((i) => (i - 1 + suggestions.length) % suggestions.length);
        return;
      }
      if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        pickMember(suggestions[activeIdx]);
        return;
      }
      if (e.key === "Escape") {
        e.preventDefault();
        setMentionOpen(false);
        return;
      }
    }

    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      onSubmit?.();
    }
  };

  return (
    <Box sx={{ position: "relative" }}>
      <textarea
        ref={textareaRef}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onClick={(e) => syncMention(value, e.target.selectionStart)}
        onFocus={(e) => syncMention(value, e.target.selectionStart)}
        placeholder={placeholder}
        disabled={disabled}
        rows={2}
        style={autoGrow ? { ...composerStyle, maxHeight: AUTO_GROW_MAX_HEIGHT, overflowY: "auto" } : composerStyle}
      />
      {mentionOpen && suggestions.length > 0 && (
        <Paper
          elevation={4}
          sx={{
            position: "absolute",
            left: 0,
            right: 0,
            bottom: "100%",
            mb: 0.5,
            maxHeight: 220,
            overflow: "auto",
            overscrollBehavior: "contain",
            borderRadius: 2,
            zIndex: 10,
            py: 0.5,
          }}
        >
          {suggestions.map((m, idx) => (
            <Box
              key={m.id}
              onMouseDown={(e) => {
                e.preventDefault();
                pickMember(m);
              }}
              sx={{
                px: 1.5,
                py: 1,
                cursor: "pointer",
                bgcolor: idx === activeIdx ? "action.selected" : "transparent",
                "&:hover": { bgcolor: "action.hover" },
              }}
            >
              <Typography variant="body2" fontWeight={700}>
                {m.fullName || m.emailLocal}
              </Typography>
              {m.email && (
                <Typography variant="caption" color="text.secondary">
                  @{m.emailLocal}
                </Typography>
              )}
            </Box>
          ))}
        </Paper>
      )}
    </Box>
  );
}
